import { Spinner } from "@material-tailwind/react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import BrandPill from "../BrandPill/BrandPill";
import BorrowingPill from "../BorrowingPill/BorrowingPill";
import StatusPill from "../StatusPill/StatusPill";
import { API_URL } from "../../../core/constants";
import { getJupiterToken } from "../../../core/cookie";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const ApplicationsList = () => {
  const [unauthorized, setUnauthorized] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const renderNotAuthorised = () => {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-6">
        <div className="w-16 h-16 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M256 0c-25.3 0-47.2 14.7-57.6 36c-7-2.6-14.5-4-22.4-4c-35.3 0-64 28.7-64 64l0 165.5-2.7-2.7c-25-25-65.5-25-90.5 0s-25 65.5 0 90.5L106.5 437c48 48 113.1 75 181 75l8.5 0 8 0c1.5 0 3-.1 4.5-.4c91.7-6.2 165-79.4 171.1-171.1c.3-1.5 .4-3 .4-4.5l0-176c0-35.3-28.7-64-64-64c-5.5 0-10.9 .7-16 2l0-2c0-35.3-28.7-64-64-64c-7.9 0-15.4 1.4-22.4 4C303.2 14.7 281.3 0 256 0zM240 96.1l0-.1 0-32c0-8.8 7.2-16 16-16s16 7.2 16 16l0 31.9 0 .1 0 136c0 13.3 10.7 24 24 24s24-10.7 24-24l0-136c0 0 0 0 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 55.9c0 0 0 .1 0 .1l0 80c0 13.3 10.7 24 24 24s24-10.7 24-24l0-71.9c0 0 0-.1 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 172.9c-.1 .6-.1 1.3-.2 1.9c-3.4 69.7-59.3 125.6-129 129c-.6 0-1.3 .1-1.9 .2l-4.9 0-8.5 0c-55.2 0-108.1-21.9-147.1-60.9L52.7 315.3c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L119 336.4c6.9 6.9 17.2 8.9 26.2 5.2s14.8-12.5 14.8-22.2L160 96c0-8.8 7.2-16 16-16c8.8 0 16 7.1 16 15.9L192 232c0 13.3 10.7 24 24 24s24-10.7 24-24l0-135.9z" />
          </svg>
        </div>
        <div className="m-auto justify-center items-center flex flex-col gap-6 text-base">
          <span>You cannot view this. You are not authorised.</span>
        </div>
      </div>
    );
  };

  // const queryClient = useQueryClient();
  const { isPending, error, data } = useQuery({
    queryKey: ["applications"],
    queryFn: async () => {
      const url = `${API_URL}/jupiter/applications`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-jupiter-token": getJupiterToken(),
        },
      });

      if (response.status === 401) {
        console.error("Unauthorized access");
        setUnauthorized(true);
        return;
      }

      return response.json();
    },
  });

  const onSelectRow = (id) => {
    console.log("Selected application ID:", id);
    navigate(`/jupiter/applications/${id}`, { replace: true });
  };

  const handleSearchKeyDown = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredData = data?.filter((application) => {
    const lastName = application?.application?.applicant?.lastName || "";
    const email = application?.application?.applicant?.email || "";
    const reference = application?.reference || "";

    return (
      lastName.toLowerCase().includes(searchTerm) ||
      email.toLowerCase().includes(searchTerm) ||
      reference.toLowerCase().includes(searchTerm)
    );
  });

  const zeropaApplicationsCount =
    filteredData?.filter(
      (application) =>
        application?.application?.brand === "zeropa" &&
        application?.status !== "test"
    ).length || 0;

  const argidApplicationsCount =
    filteredData?.filter(
      (application) =>
        application?.application?.brand === "argid" &&
        application?.status !== "test"
    ).length || 0;

  const totalForReviewAmount = filteredData?.reduce((total, application) => {
    if (
      application?.status === "for_review" &&
      application?.status !== "test"
    ) {
      return total + (application?.application?.amount || 0);
    }
    return total;
  }, 0);

  const totalApplicationAmount = filteredData?.reduce((total, application) => {
    if (
      application?.status === "for_review" ||
      (application?.status === "no_docs" && application?.status !== "test")
    ) {
      return total + (application?.application?.amount || 0);
    }
    return total;
  }, 0);

  const allApplicationsCount =
    filteredData?.filter((application) => application?.status !== "test")
      .length || 0;

  const valueFormatter = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  const renderLoading = () => {
    return (
      <div className="w-full flex min-h-screen">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <Spinner className="h-12 w-12" />
          <span>One moment...</span>
        </div>
      </div>
    );
  };

  const renderError = () => {
    return (
      <div className="w-full flex min-h-screen">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <div className="w-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
              />
            </svg>
          </div>
          <span>An error has occurred, please try again.</span>
        </div>
      </div>
    );
  };

  if (isPending) {
    return renderLoading();
  }
  if (error) {
    return renderError();
  }
  if (unauthorized) {
    return renderNotAuthorised();
  }

  return (
    <div className="applications-list">
      <div className="w-full">
        <div className="mb-8 flex items-center justify-between gap-8">
          <div>
            <h6 className="font-sans antialiased font-bold text-base md:text-lg lg:text-xl text-current">
              Applications
            </h6>
            <p className="font-sans antialiased text-base text-current mt-1">
              See a list of all applications across all ZeroPA properties.
            </p>
          </div>
        </div>

        <section className="bg-white">
          <div className="px-4 py-8 mx-auto text-center lg:px-6 mb-12 border border-slate-200 rounded-lg">
            <dl className="grid gap-8 mx-auto text-gray-900 sm:grid-cols-5">
              <div className="flex flex-col items-center justify-center">
                <dt className="mb-2 text-2xl lg:text-3xl font-extrabold">
                  {zeropaApplicationsCount}
                </dt>
                <dd className="font-light text-gray-500">
                  ZeroPA applications
                </dd>
              </div>
              <div className="flex flex-col items-center justify-center">
                <dt className="mb-2 text-2xl lg:text-3xl font-extrabold">
                  {argidApplicationsCount}
                </dt>
                <dd className="font-light text-gray-500">Argid applications</dd>
              </div>
              <div className="flex flex-col items-center justify-center">
                <dt className="mb-2 text-2xl lg:text-3xl font-extrabold">
                  {allApplicationsCount}
                </dt>
                <dd className="font-light text-gray-500">total applications</dd>
              </div>
              <div className="flex flex-col items-center justify-center">
                <dt className="mb-2 text-2xl lg:text-3xl font-extrabold">
                  &pound;
                  {valueFormatter(totalForReviewAmount)}
                </dt>
                <dd className="font-light text-gray-500">for review value</dd>
              </div>
              <div className="flex flex-col items-center justify-center">
                <dt className="mb-2 text-2xl lg:text-3xl font-extrabold">
                  &pound;
                  {valueFormatter(totalApplicationAmount)}
                </dt>
                <dd className="font-light text-gray-500">total app. value</dd>
              </div>
            </dl>
          </div>
        </section>

        <div className="w-full block relative">
          <label
            htmlFor="default-search"
            className="mb-2 text-sm font-medium text-gray-900 sr-only"
          >
            Search
          </label>
          <div className="relative">
            <input
              type="search"
              id="default-search"
              className="block w-full p-4 ps-10 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
              placeholder="Search names, emails, references..."
              value={searchTerm}
              onChange={handleSearchKeyDown}
              required
            />
          </div>
        </div>

        <div className="mt-4 w-full rounded-lg border border-slate-200">
          <table className="w-full">
            <thead className="border-b border-slate-200 bg-slate-100 text-sm font-medium text-slate-600 dark:bg-slate-900">
              <tr>
                <th className="cursor-pointer px-2.5 py-2 text-start font-medium">
                  <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                    Date
                  </small>
                </th>
                <th className="cursor-pointer px-2.5 py-2 text-start font-medium">
                  <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                    Customer
                  </small>
                </th>
                <th className="cursor-pointer px-2.5 py-2 text-start font-medium hidden lg:inline-block">
                  <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                    Property
                  </small>
                </th>
                <th className="cursor-pointer px-2.5 py-2 text-start font-medium hidden lg:inline-block">
                  <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                    Reference{" "}
                  </small>
                </th>
                <th className="cursor-pointer px-2.5 py-2 text-start font-medium hidden md:inline-block">
                  <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                    Status
                  </small>
                </th>
                <th className="cursor-pointer px-2.5 py-2 text-start font-medium">
                  <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                    Amount
                  </small>
                </th>
              </tr>
            </thead>
            <tbody className="group text-sm text-slate-800 dark:text-white">
              {filteredData
                .sort((a, b) => new Date(b.created) - new Date(a.created))
                .map((application) => (
                  <tr
                    className={`border-b border-slate-200 last:border-0 hover:bg-purple-100 hover:cursor-pointer
                      ${
                        application.status === "for_review"
                          ? "bg-green-50 border-l-2 border-l-green-500"
                          : null
                      }
                    }
                      ${
                        application.status === "no_docs"
                          ? "opacity-50 bg-gray-100 border-l-2 border-l-grey-500"
                          : null
                      }
                    } ${
                      application.status === "test"
                        ? "opacity-20 bg-gray-100 bg-grey-50 border-l-2 border-l-grey-500 blur-sm"
                        : null
                    }`}
                    onClick={() => onSelectRow(application._id)}
                    key={application._id}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                          <small className="font-sans antialiased text-sm text-current">
                            {dayjs(application.created).format(
                              "DD MMM YYYY HH:mm"
                            )}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                          <small className="font-sans antialiased text-sm text-current">
                            {application.application.applicant.lastName},{" "}
                            {application.application.applicant.firstName}
                          </small>
                          <small className="font-sans antialiased text-sm text-current opacity-70">
                            {application.application.applicant.email.toLowerCase()}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden lg:inline-block">
                      <div className="flex">
                        <BrandPill brand={application.application.brand} />
                      </div>
                    </td>
                    <td className="p-3 hidden lg:inline-block">
                      <div className="flex flex-col">
                        <small className="font-sans antialiased text-sm text-current">
                          {application.reference}
                        </small>
                      </div>
                    </td>
                    <td className="p-3 hidden md:inline-block">
                      <div className="w-max flex gap-1">
                        <StatusPill status={application.status} />
                        <BorrowingPill
                          borrowingType={
                            application.application.applicant.borrowingType
                          }
                        />
                      </div>
                    </td>
                    <td className="p-3">
                      <small className="font-sans antialiased text-sm text-current">
                        &pound;{application.application.amount}
                      </small>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {/* <div className="flex items-center justify-between border-t border-slate-200 py-4">
          <small className="font-sans antialiased text-sm text-current">
            Page 1 of 10
          </small>
          <div className="flex gap-2">
            <button
              className="inline-flex items-center justify-center border align-middle select-none font-sans font-medium text-center transition-all duration-300 ease-in disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed data-[shape=pill]:rounded-full data-[width=full]:w-full focus:shadow-none text-sm rounded-md py-1.5 px-3 shadow-sm hover:shadow bg-transparent border-slate-200 text-slate-800 hover:bg-slate-200"
              data-shape="default"
              data-width="default"
            >
              Previous
            </button>
            <button
              className="inline-flex items-center justify-center border align-middle select-none font-sans font-medium text-center transition-all duration-300 ease-in disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed data-[shape=pill]:rounded-full data-[width=full]:w-full focus:shadow-none text-sm rounded-md py-1.5 px-3 shadow-sm hover:shadow bg-transparent border-slate-200 text-slate-800 hover:bg-slate-200"
              data-shape="default"
              data-width="default"
            >
              Next
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default ApplicationsList;
